import React from 'react'
import { Radio, Typography } from 'antd'

import { VirtualCardType } from 'modules/virtual-cards/constants'
import {
  providerOptionLabel,
  VCN_PROVIDER_NAMES,
  type VcnProviderAvailability,
} from 'modules/virtual-cards/useVcnProviderAvailability'

const { Text } = Typography

const MARQETA_HINT_ID = 'vcn-provider-marqeta-hint'
/** antd radio dot (16px) + its label gap (8px) — lines the hint up under the label text. */
const RADIO_INDENT = 24

/**
 * Provider picker for VCN Management create dialogs (EMS-9720). WEX is
 * preselected; the Marqeta warning copy is attached to the option so the admin
 * reads it before selecting (stakeholder rule: warning everywhere Marqeta is
 * selectable).
 */
export function VcnProviderRadio({
  onChange,
  availability,
}: {
  onChange: (provider: VirtualCardType) => void
  /**
   * Resolved by the caller on purpose: these dialogs open via `Modal.confirm`,
   * which mounts content in a detached React root with no LaunchDarkly
   * provider above it — a flag hook called in here reads nothing and every
   * provider would look enabled.
   */
  availability: VcnProviderAvailability
}) {
  const { isWexEnabled, isMarqetaEnabled, enabledProviders } = availability
  const defaultProvider = isWexEnabled
    ? VirtualCardType.WEX
    : VirtualCardType.MARQETA
  // With one provider left there is nothing to weigh up, so the guidance goes.
  const hasChoice = enabledProviders.length > 1

  return (
    <div style={{ marginTop: 12 }}>
      <Text strong>Provider for the new card</Text>
      <Radio.Group
        defaultValue={defaultProvider}
        onChange={(e) => onChange(e.target.value)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 4,
        }}
      >
        {isWexEnabled && (
          <Radio value={VirtualCardType.WEX}>
            {providerOptionLabel(VirtualCardType.WEX, enabledProviders)}
          </Radio>
        )}
        {isMarqetaEnabled && (
          <div>
            {/* The hint sits outside the label: a block child makes the label
                report its last line as the baseline, which drops the radio dot
                next to the hint instead of the title. aria-describedby keeps
                the hint announced with the option. */}
            <Radio
              value={VirtualCardType.MARQETA}
              aria-describedby={hasChoice ? MARQETA_HINT_ID : undefined}
            >
              {VCN_PROVIDER_NAMES[VirtualCardType.MARQETA]}
            </Radio>
            {hasChoice && (
              <Text
                id={MARQETA_HINT_ID}
                type="secondary"
                style={{
                  display: 'block',
                  fontSize: 12,
                  paddingLeft: RADIO_INDENT,
                }}
              >
                WEX is the standard provider. Only continue with Marqeta if the
                carrier does not accept WEX.
              </Text>
            )}
          </div>
        )}
      </Radio.Group>
    </div>
  )
}
